'use client'

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'

import store from '@/store'

import { onboardingGroups, onboardingSteps } from './config'
import { onboardingStatusChangedEvent } from './events'
import loadOnboardingSignals, { emptyOnboardingSignals } from './services/loadOnboardingSignals'
import { OnboardingSignals, OnboardingStep } from './types'

type OnboardingContextValue = {
  groups: typeof onboardingGroups
  steps: typeof onboardingSteps
  signals: OnboardingSignals
  completedCount: number
  totalCount: number
  progress: number
  isComplete: boolean
  isLoading: boolean
  currentStep: OnboardingStep | null
  lastCheckedAt: Date | null
  refresh: () => Promise<OnboardingSignals | null>
  subscribe: () => () => void
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

type OnboardingProviderProps = {
  children: ReactNode
}

const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { currentUser } = useSnapshot(store)
  const companyId = currentUser?.selectedCompany?.id || null
  const [signals, setSignals] = useState<OnboardingSignals>(emptyOnboardingSignals)
  const [isLoading, setIsLoading] = useState(true)
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null)
  const activeRequestRef = useRef(0)
  // Signals are expensive to load (full product pagination), so they are only fetched
  // while a component actually consuming them is mounted.
  const [subscriberCount, setSubscriberCount] = useState(0)
  const isActive = subscriberCount > 0
  const loadedCompanyRef = useRef<string | null>(null)

  const subscribe = useCallback(() => {
    setSubscriberCount((count) => count + 1)
    return () => setSubscriberCount((count) => Math.max(0, count - 1))
  }, [])

  const refresh = useCallback(async () => {
    if (!companyId) return null

    const requestId = activeRequestRef.current + 1
    activeRequestRef.current = requestId
    setIsLoading(true)

    try {
      const nextSignals = await loadOnboardingSignals()
      if (activeRequestRef.current !== requestId) return null

      loadedCompanyRef.current = companyId
      setSignals(nextSignals)
      setLastCheckedAt(new Date())
      return nextSignals
    } finally {
      if (activeRequestRef.current === requestId) {
        setIsLoading(false)
      }
    }
  }, [companyId])

  useEffect(() => {
    loadedCompanyRef.current = null
    setSignals(emptyOnboardingSignals)
    setLastCheckedAt(null)
  }, [companyId])

  useEffect(() => {
    if (!companyId || !isActive) return
    if (loadedCompanyRef.current === companyId) return

    refresh()
  }, [companyId, isActive, refresh])

  useEffect(() => {
    if (!companyId || !isActive) return

    let refreshTimer: number | null = null
    const scheduleRefresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer)
      refreshTimer = window.setTimeout(refresh, 400)
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleRefresh()
    }

    window.addEventListener(onboardingStatusChangedEvent, scheduleRefresh)
    window.addEventListener('focus', scheduleRefresh)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer)
      window.removeEventListener(onboardingStatusChangedEvent, scheduleRefresh)
      window.removeEventListener('focus', scheduleRefresh)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [companyId, isActive, refresh])

  const completedCount = useMemo(
    () => onboardingSteps.filter((step) => signals[step.signalId] === true).length,
    [signals],
  )
  const totalCount = onboardingSteps.length
  const progress = Math.round((completedCount / totalCount) * 100)
  const isComplete = completedCount === totalCount
  const currentStep = onboardingSteps.find((step) => signals[step.signalId] !== true) || null

  const value = useMemo<OnboardingContextValue>(() => ({
    groups: onboardingGroups,
    steps: onboardingSteps,
    signals,
    completedCount,
    totalCount,
    progress,
    isComplete,
    isLoading,
    currentStep,
    lastCheckedAt,
    refresh,
    subscribe,
  }), [
    signals,
    completedCount,
    totalCount,
    progress,
    isComplete,
    isLoading,
    currentStep,
    lastCheckedAt,
    refresh,
    subscribe,
  ])

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)
  const subscribe = context?.subscribe

  // Registering marks the provider as active so it loads signals on demand.
  useEffect(() => subscribe?.(), [subscribe])

  if (!context) {
    throw new Error('useOnboarding must be used inside OnboardingProvider')
  }
  return context
}

export default OnboardingProvider
